import { computed, inject, onActivated, onDeactivated, provide, ref, shallowRef, watch } from 'vue'
import { useRoute } from 'vue-router'

const ASSISTANT_PAGE = Symbol('assistant-page')
const routes = {
  Dataset: ['dataset.overview', '数据总览'],
  'dataset-detail': ['dataset.detail', '数据集详情'],
  AnalysisCreate: ['analysis.configure', '标准分析配置'],
  AnalysisRun: ['analysis.result', '标准分析结果'],
  CapabilitySpace: ['capabilities.home', '能力空间'],
  ScenarioResearchProducts: ['capabilities.scenarios', '场景化研究产品'],
  Inspiration: ['inspiration.home', '灵感'],
  AiChat: ['workspace.chat', '科研对话'],
  TcmClinicalCreate: ['platform.general', '中医药临床分析配置'],
  TcmClinicalRun: ['platform.general', '中医药临床分析结果'],
  TcmMicrobiotaCreate: ['platform.general', '多组学分析配置'],
  TcmMicrobiotaDraft: ['platform.general', '多组学分析配置'],
}
const fields = ['pageKey', 'pageTitle', 'tab', 'section', 'templateSlug', 'packageVersion', 'analysisId', 'runId', 'status']

// Changing data/status or receiving a newer snapshot is not a page transition.
export const assistantPageIdentity = page => JSON.stringify(
  ['pageKey', 'routeName', 'tab', 'section', 'templateSlug', 'packageVersion', 'analysisId', 'runId'].map(key => page?.[key] || '')
)

export function createAssistantPageStore() {
  const snapshot = shallowRef(null)
  let owner = ''
  let base = {}
  let revision = 0
  const update = details => {
    const safe = {}
    for (const key of fields) {
      if (typeof details[key] === 'string' && details[key]) safe[key] = details[key].slice(0, 160)
    }
    const next = { ...base, ...safe, dataIncluded: false }
    const previous = snapshot.value
    if (previous && JSON.stringify(next) === JSON.stringify(Object.fromEntries(Object.entries(previous).filter(([key]) => !['contextRevision', 'capturedAt'].includes(key))))) return
    snapshot.value = { ...next, contextRevision: ++revision, capturedAt: Date.now() }
  }
  return {
    snapshot,
    enter(route) {
      owner = route.fullPath
      const [pageKey, pageTitle] = routes[route.name] || ['platform.general', String(route.meta?.title || '平台页面').split(' - ')[0]]
      base = { pageKey, pageTitle, routeName: String(route.name || 'Unknown'), routePath: String(route.path || route.fullPath.split('?')[0]).slice(0, 512) }
      update({})
    },
    publish(routeKey, details) { if (owner === routeKey) update(details) },
  }
}

export function provideAssistantPage(route) {
  const store = createAssistantPageStore()
  provide(ASSISTANT_PAGE, store)
  watch(() => route.fullPath, () => store.enter(route), { immediate: true, flush: 'sync' })
  return computed(() => store.snapshot.value)
}

/** Cached/hidden pages cannot publish another route's state. */
export function useAssistantPage(names, read, visible = () => true) {
  const store = inject(ASSISTANT_PAGE, null)
  const route = useRoute()
  const active = ref(true)
  onActivated(() => { active.value = true })
  onDeactivated(() => { active.value = false })
  if (!store) return
  watch([() => route.fullPath, read, active], () => {
    if (active.value && names.includes(route.name) && visible()) store.publish(route.fullPath, read())
  }, { immediate: true, deep: true, flush: 'post' })
}
